import { ADOBE_ANALYTICS_VISITOR_ID_COOKIE } from '../config/analytics.telemetry';
import { TELEMETRY_API } from '../config/api';
import { DEFAULT_INACTIVITY_PERIOD } from '../config/app';
import { APP_ID, PAGE_ID, RENDERING_ID } from '../config/data';
import { getCookie } from './cookies';
import { sharedDebounce } from './performance';

/**
 * List of user interactions occurred in quick succession, which need to be sent in a single
 * request after default user inactivity period (2.5 s)
 * @type {object[]}
 */
let interactionsPerRequest = [];

/**
 * Returns a function that makes a POST request to telemetry using provided interaction data
 * @param {object} payload User interaction data to be sent to telemetry
 * @return {function} Function that makes the api call using the passed payload
 */
const telemetryRequestUsing = payload => async () => {
	try {
		const body = JSON.stringify(payload);
		const response = await fetch(TELEMETRY_API, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
		});
		const readableResponse = await response.json();

		if (readableResponse.message !== 'OK') {
			// For better debugging
			throw new Error(`Something went wrong with telemetry api of body: ${body}`);
		}
	} catch (error) {
		console.error(error);
	}

	interactionsPerRequest = []; // Reset for next request
};

/**
 * To send user interaction/s to telemetry for analytics
 * @param {string} eventName - Specific telemetry event name
 * @param {object} eventData - Interaction data specific to an event
 * @param {string} solutionId - Unique sensor solution id used for telemetry api
 */
export const sendUserInteractionsToTelemetryFor = (eventName, eventData, solutionId) => {
	const s_ecid = decodeURIComponent(getCookie(ADOBE_ANALYTICS_VISITOR_ID_COOKIE));
	const interaction = {
		url: window.location.href,
		clientTimestamp: Date.now(),
		solutionId,
		renderingId: RENDERING_ID,
		pageId: PAGE_ID,
		s_ecid,
		event: eventName,
		event_data: eventData,
	};
	interactionsPerRequest.push(interaction);

	const interactionData = { appId: APP_ID, messages: interactionsPerRequest };

	/**
	 * Debouncing the request to make sure we make the api call for single/multiple
	 * interactions only after the default user inactivity period (2.5 s)
	 */
	const callApiOnlyAfterDelay = sharedDebounce(
		telemetryRequestUsing(interactionData),
		DEFAULT_INACTIVITY_PERIOD,
	);

	callApiOnlyAfterDelay();
};
